package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.UiContainer
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.shared.dto.ship.ReleaseSettingsDto
import co.maytes.standupbot.shared.dto.ship.UpdateReleaseSettingsRequestDto
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

enum class ReleasePanel { Page }

data class ReleaseSaveFailure(val panel: ReleasePanel, val message: String)

class ReleaseSettingsEditor(
    private val api: ApiClient,
    private val ui: UiContainer,
    private val scope: CoroutineScope,
    private val workspaceId: String?,
) {
    var loaded by mutableStateOf<ReleaseSettingsDto?>(null)
        private set
    var loadError by mutableStateOf<ApiError?>(null)
        private set
    var saving by mutableStateOf<ReleasePanel?>(null)
        private set
    var failure by mutableStateOf<ReleaseSaveFailure?>(null)
        private set
    var backfilling by mutableStateOf(false)
        private set

    suspend fun load() {
        val id = workspaceId ?: return
        loadError = null
        when (val result = api.get<ReleaseSettingsDto>(settingsPath(id))) {
            is Result.Ok -> loaded = result.value
            is Result.Err -> loadError = result.error
        }
    }

    fun reload() {
        scope.launch { load() }
    }

    fun save(panel: ReleasePanel, body: UpdateReleaseSettingsRequestDto) {
        val id = workspaceId ?: return
        if (saving != null) return
        saving = panel
        failure = null
        scope.launch {
            when (val result = api.put<UpdateReleaseSettingsRequestDto, ReleaseSettingsDto>(settingsPath(id), body)) {
                is Result.Ok -> {
                    loaded = result.value
                    ui.showToast("Release settings saved", ToastKind.Success)
                }
                is Result.Err -> {
                    failure = ReleaseSaveFailure(panel, result.error.displayMessage)
                    ui.showToast("Save failed: ${result.error.displayMessage}", ToastKind.Error)
                }
            }
            saving = null
        }
    }

    fun backfill() {
        val id = workspaceId ?: return
        if (backfilling) return
        backfilling = true
        scope.launch {
            when (val result = api.post<Unit>("/api/v1/workspaces/$id/releases/backfill")) {
                is Result.Ok -> ui.showToast("Backfill started. Merged changes appear in a minute or two.", ToastKind.Success)
                is Result.Err -> ui.showToast("Backfill failed: ${result.error.displayMessage}", ToastKind.Error)
            }
            backfilling = false
        }
    }

    private fun settingsPath(id: String) = "/api/v1/workspaces/$id/releases/settings"
}

@Composable
fun rememberReleaseSettingsEditor(): ReleaseSettingsEditor {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id?.takeIf { it.isNotBlank() }
    return remember(api, workspaceId) { ReleaseSettingsEditor(api, ui, scope, workspaceId) }
}
